import "./AdConfig.scss"
import { GridLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import newRequest from "../../../utils/newRequest";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import { useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog";
import { showToast } from "../../../toastService";
import { IoEye, IoEyeOff } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa";
import ExpandableComponent from "../../../components/expandableComponent/ExpandableComponent";
import { fetchAllUsersBySearch } from "../../../utils/genericFunctions";
import { CgLastpass } from "react-icons/cg";

const AdConfig = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);

    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [orgs, setOrgs] = useState([]);

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countries, setCountries] = useState([]);


    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);

    const [enteredResetPassword, setEnteredResetPassword] = useState(null);
    const [enteredVerifyPassword, setEnteredVerifyPassword] = useState(null);
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showVerifyPassword, setShowVerifyPassword] = useState(false);
    const [enableReset, setEnableReset] = useState(false);



    const fetchOrgs = async () => {
        try {
            const res = await newRequest.get("/org/findAll");
            const options = res.data?.map((org) => ({
                org_code: org?.org_code,
                value: org?._id,
                label: org?.org_name
            }));
            setOrgs(options);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchSupportedCountries = async () => {
        try {
            const res = await newRequest.get("/sub/countries");
            const options = res.data?.map((co) => ({
                value: co?.value,
                label: co?.label
            }));
            setCountries(options);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchOrgs();
        fetchSupportedCountries();
    }, [currentUser]);

    return (
        <motion.div className="ad-config"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            <div className="ad-config-main-wrapper">
                <div className="profile-navbar">
                    <div className="left">
                        <h1 className="page-title">{t('setConfigurations').toUpperCase()}</h1>
                    </div>
                </div>
                <ExpandableComponent
                    title={t('setOrganizationConfig')}
                    subtitle={""}
                    defaultExpanded={true}
                >
                    <div className="profile-content">
                        <div className="input-item-with-label">
                            <label htmlFor="org_code"
                                style={{ width: "130px" }}
                            >
                                {t('organization').toUpperCase()}
                            </label>
                            <Select
                                id="org_code"
                                name="org_code"
                                menuPortalTarget={document.body}
                                styles={{
                                    ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    })
                                }}
                                width="400px"
                                value={selectedOrg}
                                onChange={(selectedOption) => setSelectedOrg(selectedOption)}
                                options={orgs}
                                isSearchable={true}
                                placeholder={t('organization').toUpperCase()}
                            />
                        </div>
                        <div className="input-item-with-label">
                            <label htmlFor="country" style={{ width: "130px" }}>
                                {t('country').toUpperCase()}
                            </label>
                            <Select
                                id="country"
                                name="country"
                                menuPortalTarget={document.body}
                                styles={{
                                    ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    })
                                }}
                                width="400px"
                                value={selectedCountry}
                                onChange={(selectedOption) => setSelectedCountry(selectedOption)}
                                options={countries}
                                isSearchable={true}
                                placeholder={t('country').toUpperCase()}
                            />
                        </div>
                        <div className="input-item-with-label">
                            <label style={{ width: "130px" }} />
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                width={"400px"}
                                disabled={!(selectedCountry && selectedOrg)}
                                endIcon={<FaArrowRight />}
                                onClick={async () => {
                                    try {
                                        if (!selectedOrg?.value) {
                                            setNotifyDialogTitle("Note!");
                                            setNotifyDialogText(`Select organization!`);
                                            setIsNotifyDialogOpen(true);
                                            return;
                                        }

                                        if (!selectedCountry?.value) {
                                            setNotifyDialogTitle("Note!");
                                            setNotifyDialogText(`Select country!`);
                                            setIsNotifyDialogOpen(true);
                                            return;
                                        }

                                        setIsProcessing(true);
                                        await newRequest.post("/org/setConfiguration", {
                                            org_code: selectedOrg?.org_code,
                                            product_code: "",
                                            config: selectedCountry?.value
                                        });
                                        setIsProcessing(false);
                                        showToast(`Great. ${selectedOrg?.label} configuration set to ${selectedCountry?.label}!`, "success");
                                        setSelectedOrg(null);
                                        setSelectedCountry(null);
                                    } catch (error) {
                                        setIsProcessing(false);
                                        console.log(error);
                                    }
                                }}>
                                {t('set')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </ExpandableComponent>
                <ExpandableComponent
                    title={t('resetUserPassword')}
                    subtitle={""}
                    defaultExpanded={true}
                >
                    <div className="profile-content">
                        <div className="input-item-with-label">
                            <label htmlFor="user" style={{ width: "130px" }} >
                                {t('user').toUpperCase()}
                            </label>
                            <AsyncSelect
                                id="user"
                                name="user"
                                menuPortalTarget={document.body}
                                styles={{
                                    ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    })
                                }}
                                width="400px"
                                value={selectedUser}
                                onChange={(selectedOption) => setSelectedUser(selectedOption)}
                                loadOptions={(inputValue, callback) => fetchAllUsersBySearch(inputValue, callback, [""])}
                                isSearchable={true}
                                placeholder={t('user')}
                                noOptionsMessage={() => "Type Minimum 2 Letter to Search"}
                            />
                        </div>
                        <div className="input-item-with-label" style={{ position: "relative" }}>
                            <label htmlFor="org_code" style={{ width: "130px" }}>
                                {t('newPassword').toUpperCase()}
                            </label>
                            <input
                                id="enteredResetPassword"
                                name="enteredResetPassword"
                                type={showPassword ? "text" : "password"}
                                placeholder={t('newPassword').toUpperCase()}
                                value={enteredResetPassword}
                                className="form-input-item"
                                style={{ width: "400px", paddingRight: "30px" }}
                                onChange={(e) => setEnteredResetPassword(e.target.value)}
                                onBlur={(e) => {
                                    try {
                                        if (e.target.value.length < 5) {
                                            setPasswordError("Password must be at least 6 Characters");
                                        } else {
                                            setPasswordError("");
                                        }
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}
                            />
                            <span
                                onClick={() => setShowPassword((prev) => !prev)}
                                style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                }}
                            >
                                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                            </span>
                        </div>
                        <div className="input-item-with-label" style={{ position: "relative" }}>
                            <label htmlFor="enteredVerifyPassword" style={{ width: "130px" }}>
                                {t('verifyPassword').toUpperCase()}
                            </label>
                            <input
                                id="enteredVerifyPassword"
                                name="enteredVerifyPassword"
                                type={showVerifyPassword ? "text" : "password"}
                                placeholder={t('verifyPassword').toUpperCase()}
                                value={enteredVerifyPassword}
                                className="form-input-item"
                                style={{ width: "400px", paddingRight: "30px" }}
                                onChange={(e) => setEnteredVerifyPassword(e.target.value)}
                                onBlur={(e) => {
                                    try {
                                        if (e.target.value !== enteredResetPassword) {
                                            setPasswordError("Passwords do not match");
                                            setEnableReset(false);
                                        } else {
                                            setPasswordError("");
                                            setEnableReset(true);
                                        }
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}
                            />
                            <span
                                onClick={() => setShowVerifyPassword((prev) => !prev)}
                                style={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                }}
                            >
                                {showVerifyPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                            </span>
                        </div>
                        <div className="input-item-with-label">
                            <label style={{ width: "130px" }} />
                            {passwordError && <span className="error"> {passwordError} </span>}
                        </div>
                        <div className="input-item-with-label">
                            <label style={{ width: "130px" }} />
                            <CustomLoadingButton
                                width={"400px"}
                                disabled={!enableReset}
                                endIcon={<CgLastpass />}
                                onClick={async (e) => {
                                    try {
                                        const res = await newRequest.post("/adUser/passwordResetNote", {
                                            current_password: "",
                                            new_password: enteredVerifyPassword,
                                            user_docid: selectedUser?.value,
                                            action: "resetPassword"
                                        });
                                        // console.log(`passwordResetNote ${JSON.stringify(res?.data, null, 2)}`);
                                        if (res.data?.msg === "success") {
                                            showToast(t('passwordResetResult', { param1: selectedUser?.usr_full_name, param2: selectedUser?.usr_org_name }), "success");
                                            setEnteredResetPassword(null);
                                            setEnteredVerifyPassword(null);
                                            setSelectedUser(null);
                                            setPasswordError("");
                                            setShowPassword(false)
                                            setShowVerifyPassword(false);
                                        } else {
                                            showToast(t('passwordChangedError'), "error");
                                        }
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}>
                                {t("reset")}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </ExpandableComponent>
            </div>
            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={() => setIsNotifyDialogOpen(false)}>Close</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {isProcessing && (
                <motion.div
                    className="processing-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="processing-wrapper">
                        <GridLoader
                            color="#F44336"
                            style={{
                                height: "50px",
                                width: "50px",
                            }}
                        />
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}


export default AdConfig